import { Box, Stack } from '@mui/material';
import { useState } from 'react';

import {
  ValuesDigital,
  VenueCo2MonthValues,
} from '@/domain/project-venue-month-declaration/entities/project-venue-month-declaration';
import { FSPrimaryButton } from '@/ui/components/button/FSPrimaryButton';
import { FSTextField } from '@/ui/components/input/FSTextField';
import { language, translate } from '@/ui/i18n';

import { QuizEdition, QuizNotEdition } from '../Co2MonthQuizComponents';
import { useMonthDeclarationDisableSubmit } from './hooks/useMonthDeclarationDisableSubmit';

export function DigitalForm({
  onEdit,
  co2MonthValues,
}: {
  onEdit: (co2Values: VenueCo2MonthValues) => void;
  co2MonthValues: VenueCo2MonthValues;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [values, setValues] = useState(structuredClone(co2MonthValues.digital));

  const { disableSubmitActions } = useMonthDeclarationDisableSubmit();

  const onFormChange = (value: number, key: 'dataUsage' | 'videoViews') => {
    setValues({
      ...values,
      [key]: parseFloat(value.toString()),
    });
  };

  const handleEdit = () => {
    onEdit({ ...co2MonthValues, digital: values });
    setIsEditing(false);
  };

  const handleRemoveClick = () => {
    setIsEditing(false);
  };

  return (
    <div>
      {isEditing ? (
        <QuizEdition
          removeFromEditing={handleRemoveClick}
          title={translate(
            'ProjectsVenue.MonthCo2Quiz.Digital.how-much-digital',
          )}
          form={
            <Form
              onChange={onFormChange}
              onSubmit={handleEdit}
              values={values}
              disableSubmit={disableSubmitActions}
            />
          }
        />
      ) : (
        <QuizNotEdition
          addToEditing={() => setIsEditing(true)}
          answeredValues={[
            `${values?.dataUsage.toLocaleString(language)} Go`,
            `${values?.videoViews.toLocaleString(language)} vues`,
          ]}
          title={translate(
            'ProjectsVenue.MonthCo2Quiz.Digital.how-much-digital',
          )}
          isAnswered={
            values?.dataUsage !== undefined &&
            !Number.isNaN(values.dataUsage) &&
            values?.videoViews !== undefined &&
            !Number.isNaN(values.videoViews)
          }
        />
      )}
    </div>
  );
}

function Form({
  onChange,
  disableSubmit,
  onSubmit,
  values,
}: {
  onChange: (value: number, key: 'dataUsage' | 'videoViews') => void;
  onSubmit: () => void;
  disableSubmit: boolean;
  values: ValuesDigital;
}) {
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    key: 'dataUsage' | 'videoViews',
  ) => {
    const value = parseFloat(e.target.value);
    onChange(value, key);
  };

  const handleSubmit = () => {
    onSubmit();
  };

  return (
    <Stack>
      <FSTextField
        type="number"
        value={values.dataUsage}
        label={translate('ProjectsVenue.MonthCo2Quiz.Digital.total-data-usage')}
        onChange={(event) => handleChange(event, 'dataUsage')}
      />
      <FSTextField
        type="number"
        value={values.videoViews}
        label={translate('ProjectsVenue.MonthCo2Quiz.Digital.total-video-view')}
        onChange={(event) => handleChange(event, 'videoViews')}
      />
      <Box marginTop={2}>
        <FSPrimaryButton
          type="submit"
          disabled={disableSubmit}
          onClick={handleSubmit}
        >
          {translate('Form.save')}
        </FSPrimaryButton>
      </Box>
    </Stack>
  );
}
